import { useState } from 'react';
import { api } from '../api';
import { useLoad } from '../hooks';
import { stamp } from '../format';
import {
  Card,
  ConfirmButton,
  Empty,
  ErrorNote,
  Field,
  Loading,
  Modal,
  StatusPill,
  Table,
} from '../components/ui';
import type { Page, ResourcePool, ResourceSummary } from '../types';

/**
 * Havuzlar kaynakları zamanlama için gruplar. Bilerek kaynak türünden bağımsız:
 * bir kimlik havuzu ile bir proxy havuzu, yalnızca `type`'ı farklı olan aynı satırdır.
 */
export function Pools() {
  const pools = useLoad(() => api.get<ResourcePool[]>('/v1/resource-pools'));
  const resources = useLoad(() => api.get<Page<ResourceSummary>>('/v1/resources?limit=200'));
  const [creating, setCreating] = useState(false);
  const [renaming, setRenaming] = useState<ResourcePool | null>(null);
  const [error, setError] = useState<string | null>(null);

  const act = (work: Promise<unknown>) => {
    setError(null);
    work
      .then(() => {
        pools.reload();
        resources.reload();
      })
      .catch((reason: unknown) =>
        setError(reason instanceof Error ? reason.message : String(reason)),
      );
  };

  const rows = pools.data ?? [];
  const members = (poolId: string) =>
    (resources.data?.items ?? []).filter((r) => r.poolId === poolId && !r.deactivatedAt);

  return (
    <>
      <Card
        title={`Kaynak havuzları${rows.length ? ` (${rows.length})` : ''}`}
        flush
        actions={
          <button type="button" onClick={() => setCreating(true)}>
            Yeni havuz
          </button>
        }
        footer="Havuz silmek üyeleri gruptan çıkarır, silmez — geçmiş bir çalışmayı açıklayan hiçbir kayıt kaybolmaz."
      >
        {(error ?? pools.error) && (
          <div style={{ padding: '14px 16px 0' }}>
            <ErrorNote message={error ?? pools.error} />
          </div>
        )}
        {pools.loading && rows.length === 0 && <Loading />}
        {!pools.loading && rows.length === 0 && (
          <Empty title="Henüz havuz yok">
            Havuzu olmayan kaynaklar da normal şekilde zamanlanır.
          </Empty>
        )}
        {rows.length > 0 && (
          <Table head={['Ad', 'Tür', 'Strateji', 'Üye', 'Kullanılabilir', 'Oluşturma', '']}>
            {rows.map((pool) => (
              <tr key={pool.id}>
                <td className="cell-title">{pool.name}</td>
                <td className="muted">{pool.type === 'IDENTITY' ? 'Kimlik' : 'Proxy'}</td>
                <td className="muted small">{pool.strategy}</td>
                <td>{pool.memberCount}</td>
                <td>
                  <StatusPill
                    status={
                      pool.healthyCount > 0 ? 'ACTIVE' : pool.memberCount > 0 ? 'DEAD' : 'DISABLED'
                    }
                  />
                  <span className="muted small"> {pool.healthyCount} adet</span>
                </td>
                <td className="muted small">{stamp(pool.createdAt)}</td>
                <td className="actions">
                  <button type="button" className="ghost tiny" onClick={() => setRenaming(pool)}>
                    Düzenle
                  </button>
                  <ConfirmButton
                    label="Sil"
                    confirmLabel="Havuzu sil?"
                    onConfirm={() => act(api.del(`/v1/resource-pools/${pool.id}`))}
                  />
                </td>
              </tr>
            ))}
          </Table>
        )}
      </Card>

      {rows.map((pool) => {
        const inPool = members(pool.id);
        const candidates = (resources.data?.items ?? []).filter(
          (r) => r.type === pool.type && !r.poolId && !r.deactivatedAt,
        );
        return (
          <Card key={pool.id} title={`${pool.name} — üyeler`} flush>
            {inPool.length === 0 && <Empty title="Boş havuz" />}
            {inPool.length > 0 && (
              <Table head={['Kaynak', 'Durum', 'Eşzamanlılık', '']}>
                {inPool.map((resource) => (
                  <tr key={resource.id}>
                    <td className="cell-title">{resource.name}</td>
                    <td>
                      <StatusPill status={resource.status} />
                    </td>
                    <td className="muted">en fazla {resource.maxConcurrentLeases}</td>
                    <td className="actions">
                      <button
                        type="button"
                        className="ghost tiny"
                        onClick={() => act(api.post(`/v1/resources/${resource.id}/pool/leave`))}
                      >
                        Çıkar
                      </button>
                    </td>
                  </tr>
                ))}
              </Table>
            )}
            {candidates.length > 0 && (
              <div className="inline-form">
                <select
                  defaultValue=""
                  onChange={(e) => {
                    if (e.target.value)
                      act(api.post(`/v1/resources/${e.target.value}/pool/${pool.id}`));
                    e.target.value = '';
                  }}
                >
                  <option value="">
                    {pool.type === 'IDENTITY' ? 'Kimlik ekle…' : 'Proxy ekle…'}
                  </option>
                  {candidates.map((resource) => (
                    <option key={resource.id} value={resource.id}>
                      {resource.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </Card>
        );
      })}

      {creating && (
        <PoolForm
          onClose={() => setCreating(false)}
          onDone={() => {
            setCreating(false);
            pools.reload();
          }}
        />
      )}
      {renaming && (
        <PoolForm
          pool={renaming}
          onClose={() => setRenaming(null)}
          onDone={() => {
            setRenaming(null);
            pools.reload();
          }}
        />
      )}
    </>
  );
}

function PoolForm({
  pool,
  onClose,
  onDone,
}: {
  pool?: ResourcePool;
  onClose: () => void;
  onDone: () => void;
}) {
  const [name, setName] = useState(pool?.name ?? '');
  const [type, setType] = useState<'IDENTITY' | 'PROXY'>(pool?.type ?? 'PROXY');
  const [strategy, setStrategy] = useState(pool?.strategy ?? 'least-load');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = () => {
    setBusy(true);
    setError(null);
    const work = pool
      ? api.patch(`/v1/resource-pools/${pool.id}`, { name, strategy })
      : api.post('/v1/resource-pools', { name, type, strategy });
    work
      .then(onDone)
      .catch((reason: unknown) =>
        setError(reason instanceof Error ? reason.message : String(reason)),
      )
      .finally(() => setBusy(false));
  };

  return (
    <Modal
      title={pool ? `${pool.name} havuzunu düzenle` : 'Yeni havuz'}
      submitLabel={pool ? 'Kaydet' : 'Oluştur'}
      onClose={onClose}
      onSubmit={submit}
      busy={busy}
      error={error}
    >
      <Field label="Ad">
        <input value={name} onChange={(e) => setName(e.target.value)} required />
      </Field>
      <Field label="Tür" hint={pool ? 'Üyesi olan bir havuzun türü değiştirilemez.' : undefined}>
        <select
          value={type}
          disabled={Boolean(pool)}
          onChange={(e) => setType(e.target.value as 'IDENTITY' | 'PROXY')}
        >
          <option value="PROXY">Proxy</option>
          <option value="IDENTITY">Kimlik</option>
        </select>
      </Field>
      <Field label="Strateji" hint="Bugün uygulanan tek strateji least-load.">
        <select value={strategy} onChange={(e) => setStrategy(e.target.value)}>
          <option value="least-load">least-load (en az yüklü)</option>
        </select>
      </Field>
    </Modal>
  );
}
